import React from 'react';
import { useTranslation } from 'react-i18next';

const StatPill = ({ label, value }) => (
  <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
    <div className="text-[10px] font-extrabold uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-sm font-extrabold text-gray-900 tabular-nums mt-0.5">{value}</div>
  </div>
);

export default function RecentContextPanel({ recentContext }) {
  const { t } = useTranslation();
  if (!recentContext) return null;

  const last7 = recentContext.last_7_days || null;
  const last28 = recentContext.last_28_days_avg_week || null;
  const prev = Array.isArray(recentContext.previous_sessions) ? recentContext.previous_sessions : [];

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white">
      <div className="text-xs font-extrabold uppercase tracking-wide text-gray-600">
        {t('workout.recent_context')}
      </div>

      {last7 ? (
        <>
          <div className="text-sm font-bold text-gray-800 mt-3">{t('workout.last_7_days')}</div>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatPill label={t('workout.distance')} value={`${last7.distance_km ?? 0} km`} />
            <StatPill label={t('workout.duration')} value={`${last7.duration_min ?? 0} min`} />
            <StatPill label={t('workout.elevation')} value={`+${last7.elevation_gain_m ?? 0} m`} />
            <StatPill label={t('workout.hard_sessions')} value={`${last7.hard_sessions ?? 0}`} />
          </div>
          {last7.intensity_distribution_pct ? (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {['Z1', 'Z2', 'Z3', 'Z4', 'Z5'].map((z) => (
                <div key={z} className="border border-gray-200 rounded-lg p-2 text-center">
                  <div className="text-[10px] font-extrabold text-gray-500">{z}</div>
                  <div className="text-sm font-bold tabular-nums">{last7.intensity_distribution_pct[z] ?? 0}%</div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {last28 ? (
        <>
          <div className="text-sm font-bold text-gray-800 mt-5">{t('workout.last_28_days_avg_week')}</div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <StatPill label={t('workout.distance')} value={`${last28.distance_km ?? 0} km`} />
            <StatPill label={t('workout.duration')} value={`${last28.duration_min ?? 0} min`} />
            <StatPill label={t('workout.elevation')} value={`+${last28.elevation_gain_m ?? 0} m`} />
          </div>
        </>
      ) : null}

      {recentContext.monotony_proxy ? (
        <div className="mt-4 text-sm text-gray-700">
          <span className="font-bold">{t('workout.monotony_proxy')}:</span> {recentContext.monotony_proxy}
        </div>
      ) : null}

      {prev.length > 0 ? (
        <div className="mt-5">
          <div className="text-sm font-bold text-gray-800">{t('workout.previous_sessions')}</div>
          <div className="mt-2 space-y-2">
            {prev.slice(0, 3).map((s, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500 font-mono">{s.date}</div>
                    <div className="font-bold text-gray-900 truncate">{s.title || s.type}</div>
                  </div>
                  <div className="text-xs font-bold text-gray-600 tabular-nums whitespace-nowrap">
                    {s.distance_km ? `${s.distance_km} km` : null}
                    {s.duration_min ? ` • ${s.duration_min} min` : null}
                  </div>
                </div>
                {s.notes ? <div className="text-sm text-gray-700 mt-2 italic">"{s.notes}"</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


